"use client";

import { useMemo } from "react";
import { useAppSelector } from "@/store";
import { getBrandKeyFromEnv, isSystems247Brand } from "@/lib/branding";
import {
  getFallbackUiLanguageOptions,
  getUiLanguageOptionByLocale,
  getUiLanguageShortLabel,
  normalizeUiLanguageOptions,
  SYSTEMS247_UI_LANGUAGES,
  TRUSTEXAM_EXTRA_UI_LANGUAGES,
  type UiLanguageOption,
} from "@/shared/i18n/languages";

const SYSTEMS247_UI_LANGUAGE_OPTIONS = normalizeUiLanguageOptions(SYSTEMS247_UI_LANGUAGES);
const TRUSTEXAM_EXTRA_UI_LANGUAGE_OPTIONS = normalizeUiLanguageOptions(TRUSTEXAM_EXTRA_UI_LANGUAGES);

function withTrustexamExtras(options: UiLanguageOption[]): UiLanguageOption[] {
  if (getBrandKeyFromEnv() !== "trustexam") return options;

  const present = new Set(options.map((option) => option.locale));
  const extras = TRUSTEXAM_EXTRA_UI_LANGUAGE_OPTIONS.filter((option) => !present.has(option.locale));

  return extras.length ? [...options, ...extras] : options;
}

export function useUiLanguageOptions(): UiLanguageOption[] {
  const options = useAppSelector((state) => state.uiLanguages.options);

  return useMemo(() => {
    if (isSystems247Brand()) {
      return SYSTEMS247_UI_LANGUAGE_OPTIONS;
    }

    if (Array.isArray(options) && options.length > 0) {
      return options;
    }

    return getFallbackUiLanguageOptions();
  }, [options]);
}

export function useInterfaceLanguageOptions(): UiLanguageOption[] {
  const options = useUiLanguageOptions();

  return useMemo(() => withTrustexamExtras(options), [options]);
}

export function useUiLanguageShortLabel(locale: string | null | undefined): string {
  const options = useUiLanguageOptions();

  return useMemo(() => {
    return getUiLanguageOptionByLocale(options, locale)?.short
      ?? getUiLanguageShortLabel(locale ?? "");
  }, [locale, options]);
}
